import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Paper,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAPILoader } from "../../../state/actions/globalAction";
import { getTestModule, getUserList } from "../../../services/websiteApi";
import {
  listRiskAssessments,
  listVitals,
  getRiskAssessmentById,
} from "../../../services/v5Api";
import {
  dateFormatHanlder,
  userInfoFetch,
  formatName,
} from "../../../utlis/commonFunction";
import { getParams, searchParams, handleParams } from "../../../utlis/helpers";
import {
  ACTIVITY_MONITORING,
  EDIT_TEST_PERSON,
  PERSON_DASHBOARD,
  TUG_FRONT_INFO,
  TUG_SIDE_INFO,
} from "../../../routes/routerPath";
import { getPatientDetails } from "../../mmtUserList/mmtApi";
import { getStoredSession } from "../../mmtUserList/mmtFlow";
import { hasRequiredTestPersonMeasurements } from "../testModule/preTestProfileValidation";

/** Shown under Metrics → exercise measurement only (TUG Side + TUG Front). */
const MEASUREMENT_MODULE_IDS = new Set([1, 4]);
import { pageLocale, getCurrentLocale } from "../../../utlis/locale";
import MoreActionMenu from "../MoreActionMenu";
import { RiskQuestionnaire } from "./riskQuestionnaire";
import { MobilityInsights, AssessmentList } from "./mobilityInsights";
import { CarePlan } from "./carePlan";
import { CarePlanV2 } from "./carePlanV2";
import { FEATURE_FLAGS } from "../../../configs/featureFlags";
import { rewriteReportDomain } from "../../../configs/environment";
import useVitalsListener from "../../../hooks/useVitalsListener";
import { isWebView, postWebViewMessage } from "../../../utlis/webViewBridge";
import "./persondashboard.scss";

// Tab configuration
const TABS = [
  { id: "risk", label: "Risk", icon: "risk" },
  { id: "metrics", label: "Metrics", icon: "metrics" },
  { id: "monitor", label: "Monitor", icon: "monitor" },
  { id: "care", label: "Care", icon: "care" },
];

const PersonDashboard = () => {
  const [moduleData, setModuleData] = useState([]);
  const [activeTab, setActiveTab] = useState("risk");
  // Incremented after sessionStorage is updated so userInfoFetch() re-runs with fresh data.
  const [, setSessionFlush] = useState(0);
  const [badges, setBadges] = useState({ risk: false, care: false });
  const navTouchStartXRef = useRef(null);
  const navTouchStartYRef = useRef(null);
  const navTouchEndXRef = useRef(null);
  const navTouchEndYRef = useRef(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);
  const [showMeasurementPrompt, setShowMeasurementPrompt] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showMobilityInsights, setShowMobilityInsights] = useState(false);
  const [showAssessmentList, setShowAssessmentList] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const contentRef = useRef(null);
  const suppressReportDeepLinkRef = useRef(false);

  /* eslint-disable no-unused-vars */
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  /* eslint-enable no-unused-vars */

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    accessToken,
    providerId,
    adminId,
    clientId,
    seniorId,
    tab: tabFromUrl,
    reportId: reportIdFromUrl,
    hideNav: hideNavParam,
  } = getParams();
  // Hosts (e.g. MMT WebView) can request a chrome-less embed via ?hideNav=true
  // — bottom tab bar is hidden and tab switching is suppressed.
  const isNavHidden = hideNavParam === "true" || hideNavParam === "1";
  const userInfo = userInfoFetch();

  // Handle vitals success
  const handleVitalsSuccess = useCallback(() => {
    setSnackbarMessage("Vitals saved successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  }, []);

  // Handle vitals error
  const handleVitalsError = useCallback(() => {
    setSnackbarMessage("Failed to save vitals. Please try again.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }, []);

  // Listen for Vitals from PostMessage
  useVitalsListener(
    userInfo?.id || seniorId,
    handleVitalsSuccess,
    handleVitalsError,
  );

  // Locale strings
  const {
    get_risk_insights,
    exercise_measurement_modules,
    check_vitals,
    activity_analysis,
    mobility_insights,
    mobility_insights_desc,
    view_recent_result,
    take_risk_test,
    view_recent_report,
    conduct_measurement,
    check_vitals_desc,
    check_vitals_btn,
    activity_analysis_desc,
    view_alerts_activities,
    last_tested,
    total_tests,
    not_available,
    height_required,
    height_required_description,
    add_height,
    cancel,
    profile_check_failed,
  } = pageLocale("personDashboard");

  // Bottom-nav swipe gesture (only on the nav bar)
  const minSwipeDistance = 40;

  const onNavTouchStart = (e) => {
    navTouchEndXRef.current = null;
    navTouchEndYRef.current = null;
    navTouchStartXRef.current = e.targetTouches[0].clientX;
    navTouchStartYRef.current = e.targetTouches[0].clientY;
  };

  const onNavTouchMove = (e) => {
    navTouchEndXRef.current = e.targetTouches[0].clientX;
    navTouchEndYRef.current = e.targetTouches[0].clientY;
  };

  const onNavTouchEnd = () => {
    const startX = navTouchStartXRef.current;
    const startY = navTouchStartYRef.current;
    const endX = navTouchEndXRef.current;
    const endY = navTouchEndYRef.current;

    if (startX == null || startY == null || endX == null || endY == null) {
      return;
    }

    const dx = startX - endX;
    const dy = startY - endY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Only treat as a swipe if it is clearly horizontal.
    const isHorizontalSwipe =
      absDx > minSwipeDistance && (absDy === 0 || absDx / absDy > 1.5);
    if (!isHorizontalSwipe) return;

    const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);
    const isLeftSwipe = dx > minSwipeDistance;
    const isRightSwipe = dx < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < TABS.length - 1) {
      goToTab(TABS[currentIndex + 1].id);
    } else if (isRightSwipe && currentIndex > 0) {
      goToTab(TABS[currentIndex - 1].id);
    }
  };

  const goToTab = (tabId) => {
    setActiveTab(tabId);
    // Keep all existing query params (accessToken, etc.) and only update `tab`.
    navigate(PERSON_DASHBOARD + handleParams("tab", tabId));
  };

  const setDashboardQueryParams = (paramsToSet) => {
    const urlParams = new URLSearchParams(window.location.search);
    Object.entries(paramsToSet || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        urlParams.delete(key);
      } else {
        urlParams.set(key, String(value));
      }
    });
    navigate(PERSON_DASHBOARD + "?" + urlParams.toString());
  };

  useEffect(() => {
    if (tabFromUrl && TABS.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Function to load test modules
  const loadTestModules = useCallback(
    (userInfo) => {
      if (!userInfo?.id || !userInfo?.seniorId) {
        dispatch(setAPILoader(false));
        return;
      }

      getTestModule(userInfo.id, userInfo.seniorId)
        .then((res) => {
          const filteredModules = (res?.data?.data || []).filter((module) =>
            MEASUREMENT_MODULE_IDS.has(module.moduleId),
          );
          setModuleData(filteredModules);
          sessionStorage.setItem(
            "testMModule",
            JSON.stringify(filteredModules),
          );
          dispatch(setAPILoader(false));
        })
        .catch(() => {
          dispatch(setAPILoader(false));
        });
    },
    [dispatch],
  );

  // Initialize data
  useEffect(() => {
    dispatch(setAPILoader(true));

    let currentUserInfo = userInfoFetch();

    const exitToHost = () => {
      dispatch(setAPILoader(false));
      if (isWebView()) {
        setSnackbarMessage("Invalid senior. Redirecting…");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setTimeout(() => {
          postWebViewMessage("BACK_TO_USER_LIST");
        }, 1500);
      } else {
        navigate(-1);
      }
    };

    const seniorIdMismatch =
      seniorId && String(currentUserInfo?.id) !== String(seniorId);

    if (seniorIdMismatch || (!currentUserInfo?.id && seniorId)) {
      getUserList(1, 999)
        .then((res) => {
          const users = res?.data?.data || [];
          sessionStorage.setItem("userList", JSON.stringify(users));

          const foundIndex = users.findIndex(
            (user) => String(user.id) === String(seniorId),
          );

          if (foundIndex !== -1) {
            sessionStorage.setItem("userIndex", foundIndex);
            currentUserInfo = users[foundIndex];
            setSessionFlush((c) => c + 1);
            loadTestModules(currentUserInfo);
          } else {
            exitToHost();
          }
        })
        .catch(() => {
          exitToHost();
        });
    } else if (currentUserInfo?.id) {
      loadTestModules(currentUserInfo);
    } else {
      exitToHost();
    }

    // Simulate badge indicators (replace with real API data)
    setBadges({ risk: true, care: false });
  }, [dispatch, seniorId, navigate, loadTestModules]);

  const handleBackClick = () => {
    if (isWebView()) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "BACK_TO_USER_LIST",
        }),
      );
    } else {
      navigate(-1);
    }
  };

  const startMeasurement = (moduleId, moduleData) => {
    sessionStorage.setItem("romStatus", "noenter");
    sessionStorage.setItem("testDescription", JSON.stringify(moduleData));

    if (moduleId === 1) {
      navigate(TUG_SIDE_INFO + `${searchParams}`);
    } else if (moduleId === 4) {
      navigate(TUG_FRONT_INFO + `${searchParams}`);
    }
  };

  const handleConductMeasurement = async (moduleId, isAllowed, moduleData) => {
    if (!isAllowed || isCheckingProfile) return;

    const seniorUuid = userInfo?.id;
    const session = getStoredSession();
    if (seniorUuid && session) {
      setIsCheckingProfile(true);
      try {
        const profile = await getPatientDetails(session, seniorUuid);
        if (!hasRequiredTestPersonMeasurements(profile)) {
          setShowMeasurementPrompt(true);
          return;
        }
      } catch (error) {
        setSnackbarMessage(
          error?.response?.data?.message ||
            error?.message ||
            profile_check_failed,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      } finally {
        setIsCheckingProfile(false);
      }
    }

    startMeasurement(moduleId, moduleData);
  };

  const handleAddMeasurements = () => {
    const params = new URLSearchParams(window.location.search);
    if (userInfo?.id) {
      params.set("seniorUuid", userInfo.id);
    }
    params.set("focus", "height");
    setShowMeasurementPrompt(false);
    navigate(`${EDIT_TEST_PERSON}?${params.toString()}`);
  };

  const handleViewReport = (url, moduleId, e) => {
    e.stopPropagation();
    if (url) {
      let reportUrl = url;
      if (moduleId === 1) {
        reportUrl = url.replace(/side-tug-reportv1/g, "side-tug-reportv2");
      }
      reportUrl = rewriteReportDomain(reportUrl);
      window.location.href = `${reportUrl}&isWebsite=true&accessToken=${accessToken}&providerId=${providerId}&adminId=${adminId}&clientId=${clientId}`;
    }
  };

  const handleViewRecentResult = async () => {
    dispatch(setAPILoader(true));
    try {
      const response = await listRiskAssessments(userInfo?.id);
      const fetchedAssessments = response?.data || response || [];

      if (fetchedAssessments.length > 0) {
        // Sort assessments by date (most recent first)
        const sortedAssessments = [...fetchedAssessments].sort(
          (a, b) =>
            new Date(b.assessedAt || b.createdAt) -
            new Date(a.assessedAt || a.createdAt),
        );
        setAssessments(sortedAssessments);
      } else {
        // No assessments found - show the list with empty state
        setAssessments([]);
      }
      setShowAssessmentList(true);
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setAssessments([]);
      setShowAssessmentList(true);
    } finally {
      dispatch(setAPILoader(false));
    }
  };

  /* eslint-enable no-unused-vars */
  // Need to import getRiskAssessmentById first if not imported
  // But wait, I need to check imports specifically.
  // I will assume I can add the import or it is already available?
  // Looking at imports: `listRiskAssessments` is there. I need to add `getRiskAssessmentById`.

  const handleViewAssessmentReport = async (assessment) => {
    if (assessment?.id) {
      suppressReportDeepLinkRef.current = true;
      setDashboardQueryParams({
        tab: "risk",
        reportId: assessment.id,
      });
    }
    dispatch(setAPILoader(true));
    try {
      const response = await getRiskAssessmentById(assessment.id, userInfo.id);
      const detailData = response.data || response;
      setSelectedAssessment({ ...assessment, ...detailData });
    } catch (err) {
      console.error("Error fetching assessment details:", err);
      // Fallback
      setSelectedAssessment(assessment);
    } finally {
      dispatch(setAPILoader(false));
      setShowAssessmentList(false);
      setShowMobilityInsights(true);
    }
  };

  const handleAssessmentListClose = () => {
    setShowAssessmentList(false);
    setAssessments([]);
  };

  const handleOpenMobilityInsightsByReportId = async (
    reportId,
    syncUrl = true,
  ) => {
    if (!reportId) return;
    if (syncUrl) {
      suppressReportDeepLinkRef.current = true;
      setDashboardQueryParams({
        tab: "risk",
        reportId,
      });
    }
    dispatch(setAPILoader(true));
    try {
      const seniorUuid = userInfo?.id || seniorId;
      const response = await getRiskAssessmentById(reportId, seniorUuid);
      const detailData = response?.data || response;
      setSelectedAssessment(detailData);
    } catch (err) {
      console.error("Error fetching risk assessment by report id:", err);
      setSelectedAssessment({ id: reportId });
    } finally {
      dispatch(setAPILoader(false));
      setShowAssessmentList(false);
      setShowQuestionnaire(false);
      setShowMobilityInsights(true);
    }
  };

  // Deep link support: /person-dashboard?tab=risk&reportId=<id>
  useEffect(() => {
    if (!reportIdFromUrl) return;
    if (suppressReportDeepLinkRef.current) {
      suppressReportDeepLinkRef.current = false;
      return;
    }
    if (showMobilityInsights && selectedAssessment?.id === reportIdFromUrl) {
      return;
    }
    setActiveTab("risk");
    handleOpenMobilityInsightsByReportId(reportIdFromUrl, false);
  }, [reportIdFromUrl]);

  const handleTakeRiskTest = () => {
    // Directly show the questionnaire
    setShowQuestionnaire(true);
  };

  const handleMobilityInsightsClose = () => {
    setDashboardQueryParams({
      tab: "risk",
      reportId: null,
    });
    setShowMobilityInsights(false);
    setSelectedAssessment(null);
    // If we have assessments loaded, go back to list view
    if (assessments.length > 0) {
      setShowAssessmentList(true);
    }
    // Update badge to show assessment completed
    setBadges((prev) => ({ ...prev, risk: false }));
  };

  const handleQuestionnaireComplete = (submitData) => {
    console.log("Questionnaire completed:", submitData);
    setShowQuestionnaire(false);
    // Update badge to show assessment completed
    setBadges((prev) => ({ ...prev, risk: false }));
    // TODO: Show success message or navigate to results
  };

  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
  };

  const handleCheckVitals = async () => {
    if (isWebView()) {
      dispatch(setAPILoader(true));
      try {
        const response = await listVitals(userInfo?.id || seniorId);
        const data = response?.data || response || [];

        if (Array.isArray(data) && data.length > 0) {
          // Sort by timestamp desc (or createdAt if timestamp missing)
          const sortedData = [...data].sort(
            (a, b) =>
              new Date(b.timestamp || b.createdAt) -
              new Date(a.timestamp || a.createdAt),
          );
          const latestVital = sortedData[0];

          postWebViewMessage("OPEN_VITALS_PREVIEW", latestVital?.vitals);
        } else {
          // No data, start new test
          postWebViewMessage("START_VITALS_TEST");
        }
      } catch (error) {
        console.error("Error fetching vitals:", error);
        // Fallback to start test on error
        postWebViewMessage("START_VITALS_TEST");
      } finally {
        dispatch(setAPILoader(false));
      }
    } else {
      console.log("Vitals feature requires the mobile app");
    }
  };

  const handleViewAlertsActivities = () => {
    navigate(ACTIVITY_MONITORING + searchParams);
  };

  // Tab icon components
  const TabIcon = ({ type, isActive }) => {
    const color = isActive ? "var(--primary-color)" : "black";

    const icons = {
      risk: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
            fill={color}
          />
        </svg>
      ),
      metrics: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 9.2H8V19H5V9.2ZM10.6 5H13.4V19H10.6V5ZM16.2 13H19V19H16.2V13Z"
            fill={color}
          />
        </svg>
      ),
      monitor: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
            fill={color}
          />
        </svg>
      ),
      care: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19 3H14.82C14.4 1.84 13.3 1 12 1C10.7 1 9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 3C12.55 3 13 3.45 13 4C13 4.55 12.55 5 12 5C11.45 5 11 4.55 11 4C11 3.45 11.45 3 12 3ZM14 17H7V15H14V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z"
            fill={color}
          />
        </svg>
      ),
    };

    return icons[type] || null;
  };

  // Render Risk Tab Content
  const renderRiskContent = () => (
    <div className="tab-content-inner">
      <section className="dashboard-section">
        <Typography className="section-title">{get_risk_insights}</Typography>
        <Paper elevation={0} className="dashboard-card feature-card">
          <div className="card-header">
            <div className="card-icon mobility-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5 5.5C14.6 5.5 15.5 4.6 15.5 3.5C15.5 2.4 14.6 1.5 13.5 1.5C12.4 1.5 11.5 2.4 11.5 3.5C11.5 4.6 12.4 5.5 13.5 5.5ZM9.8 8.9L7 23H9.1L10.9 15L13 17V23H15V15.5L12.9 13.5L13.5 10.5C14.8 12 16.8 13 19 13V11C17.1 11 15.5 10 14.7 8.6L13.7 7C13.3 6.4 12.7 6 12 6C11.7 6 11.5 6.1 11.2 6.1L6 8.3V13H8V9.6L9.8 8.9Z"
                  fill="var(--primary-color)"
                />
              </svg>
            </div>
            <div className="card-title-content">
              <Typography className="card-title">
                {mobility_insights}
              </Typography>
              <Typography className="card-subtitle">
                {mobility_insights_desc}
              </Typography>
            </div>
          </div>
          <div className="card-actions">
            <button
              className="btn btn-outline"
              onClick={handleViewRecentResult}
            >
              {view_recent_result}
            </button>
            <button className="btn btn-primary" onClick={handleTakeRiskTest}>
              {take_risk_test}
            </button>
          </div>
        </Paper>
      </section>
    </div>
  );

  // Render Metrics Tab Content
  const renderMetricsContent = () => (
    <div className="tab-content-inner">
      <section className="dashboard-section">
        <Typography className="section-title">
          {exercise_measurement_modules}
        </Typography>
        <div className="modules-list">
          {moduleData.map((module) => (
            <Paper
              key={module.moduleId}
              elevation={0}
              className={`dashboard-card module-card ${!module.isOperationAllowed ? "disabled" : ""}`}
              onClick={() =>
                handleConductMeasurement(
                  module.moduleId,
                  module.isOperationAllowed,
                  module,
                )
              }
            >
              <div className="card-header">
                <div className="card-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 5.5C14.6 5.5 15.5 4.6 15.5 3.5C15.5 2.4 14.6 1.5 13.5 1.5C12.4 1.5 11.5 2.4 11.5 3.5C11.5 4.6 12.4 5.5 13.5 5.5ZM9.8 8.9L7 23H9.1L10.9 15L13 17V23H15V15.5L12.9 13.5L13.5 10.5C14.8 12 16.8 13 19 13V11C17.1 11 15.5 10 14.7 8.6L13.7 7C13.3 6.4 12.7 6 12 6C11.7 6 11.5 6.1 11.2 6.1L6 8.3V13H8V9.6L9.8 8.9Z"
                      fill="var(--primary-color)"
                    />
                  </svg>
                </div>
                <div className="card-title-content">
                  <Typography className="card-title">
                    {module.moduleName}
                  </Typography>
                  <Typography className="card-subtitle">
                    {module.description}
                  </Typography>
                </div>
                <div className="card-arrow">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z"
                      fill="#9E9E9E"
                    />
                  </svg>
                </div>
              </div>
              {module.totalTest != null &&
                (module.lastTestTaken != null || module.totalTest > 0) && (
                  <div className="module-stats">
                    <div className="stat-item">
                      <Typography className="stat-label">
                        {last_tested}
                      </Typography>
                      <Typography className="stat-value">
                        {module.lastTestTaken
                          ? dateFormatHanlder(
                              module.lastTestTaken,
                              getCurrentLocale(),
                            )
                          : not_available}
                      </Typography>
                    </div>
                    <div className="stat-item">
                      <Typography className="stat-label">
                        {total_tests}
                      </Typography>
                      <Typography className="stat-value">
                        {module.totalTest}
                      </Typography>
                    </div>
                  </div>
                )}
              <div className="card-actions">
                {module.isReportAvailable && (
                  <button
                    className="btn btn-outline"
                    onClick={(e) =>
                      handleViewReport(module.reportURL, module.moduleId, e)
                    }
                  >
                    {view_recent_report}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConductMeasurement(
                      module.moduleId,
                      module.isOperationAllowed,
                      module,
                    );
                  }}
                  disabled={!module.isOperationAllowed || isCheckingProfile}
                >
                  {conduct_measurement}
                </button>
              </div>
            </Paper>
          ))}
        </div>
      </section>
    </div>
  );

  // Render Monitor Tab Content
  const renderMonitorContent = () => (
    <div className="tab-content-inner">
      {/* Vital Signs Section */}
      <section className="dashboard-section">
        <Typography className="section-title">{check_vitals}</Typography>
        <Paper elevation={0} className="dashboard-card feature-card">
          <Typography className="card-description">
            {check_vitals_desc}
          </Typography>
          <div className="card-actions">
            <button className="btn btn-primary" onClick={handleCheckVitals}>
              {check_vitals_btn}
            </button>
          </div>
        </Paper>
      </section>

      {/* Activity Analysis Section */}
      <section className="dashboard-section">
        <Typography className="section-title">{activity_analysis}</Typography>
        <Paper elevation={0} className="dashboard-card feature-card">
          <Typography className="card-description">
            {activity_analysis_desc}
          </Typography>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              onClick={handleViewAlertsActivities}
            >
              {view_alerts_activities}
            </button>
          </div>
        </Paper>
      </section>
    </div>
  );

  // Render Care Tab Content
  const renderCareContent = () => (
    <div className="tab-content-inner">
      {FEATURE_FLAGS.ENABLE_V2_CARE_PLAN ? (
        <CarePlanV2
          onNavigateTab={setActiveTab}
          onOpenMobilityInsights={handleOpenMobilityInsightsByReportId}
        />
      ) : (
        <CarePlan
          onNavigateTab={setActiveTab}
          onOpenMobilityInsights={handleOpenMobilityInsightsByReportId}
        />
      )}
    </div>
  );

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "risk":
        return renderRiskContent();
      case "metrics":
        return renderMetricsContent();
      case "monitor":
        return renderMonitorContent();
      case "care":
        return renderCareContent();
      default:
        return renderRiskContent();
    }
  };

  // Show loading state when fetching assessment

  // Render assessment list if active
  if (showAssessmentList) {
    return (
      <AssessmentList
        assessments={assessments}
        onClose={handleAssessmentListClose}
        onViewReport={handleViewAssessmentReport}
        onRetake={() => {
          setShowAssessmentList(false);
          setShowQuestionnaire(true);
        }}
      />
    );
  }

  // Render mobility insights (detail view) if active
  if (showMobilityInsights) {
    return (
      <MobilityInsights
        assessmentData={selectedAssessment}
        seniorId={userInfo?.id || "unknown"}
        onClose={handleMobilityInsightsClose}
        onRetake={() => {
          setShowMobilityInsights(false);
          setSelectedAssessment(null);
          setShowQuestionnaire(true);
        }}
      />
    );
  }

  // Render questionnaire if active
  if (showQuestionnaire) {
    return (
      <RiskQuestionnaire
        seniorId={userInfo?.id || "unknown"}
        onComplete={handleQuestionnaireComplete}
        onClose={handleQuestionnaireClose}
      />
    );
  }

  return (
    <div className={`person-dashboard-v2${isNavHidden ? " nav-hidden" : ""}`}>
      {/* Pinned Header */}
      <div className="pinned-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBackClick}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5m0 0l7-7m-7 7l7 7"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div
            className="user-info"
            onClick={() => {
              if (isWebView() && userInfo?.id) {
                postWebViewMessage("OPEN_VIEW_TEST_PERSON", userInfo.id);
              }
            }}
            style={{ cursor: isWebView() ? "pointer" : "default" }}
          >
            <img src={userInfo?.picture} className="user-avatar" alt="user" />
            <div className="user-details">
              <Typography className="user-name">
                {formatName(userInfo, getCurrentLocale())}
              </Typography>
              <Typography className="user-meta">
                {userInfo?.gender}
                {userInfo?.age > 0 && ` | ${userInfo?.age}`}
              </Typography>
            </div>
          </div>
        </div>
        <div className="header-right">
          <MoreActionMenu userInfo={userInfo} />
        </div>
      </div>

      {/* Swipeable Content Area */}
      <div className="tab-content" ref={contentRef}>
        {renderTabContent()}
      </div>

      {/* Bottom Navigation — hidden when host passes ?hideNav=true */}
      {!isNavHidden && (
        <nav
          className="bottom-nav"
          onTouchStart={onNavTouchStart}
          onTouchMove={onNavTouchMove}
          onTouchEnd={onNavTouchEnd}
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => goToTab(tab.id)}
            >
              <div className="nav-icon-wrapper">
                <TabIcon type={tab.icon} isActive={activeTab === tab.id} />
                {badges[tab.id] && <span className="badge-dot" />}
              </div>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>
      )}

      <Dialog
        open={showMeasurementPrompt}
        onClose={() => setShowMeasurementPrompt(false)}
        aria-labelledby="measurement-required-title"
      >
        <DialogTitle id="measurement-required-title">
          {height_required}
        </DialogTitle>
        <DialogContent>{height_required_description}</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMeasurementPrompt(false)}>
            {cancel}
          </Button>
          <Button variant="contained" onClick={handleAddMeasurements} autoFocus>
            {add_height}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PersonDashboard;
